// Libraries
import moment from 'moment'
import React from 'react'

// Components
import Typography from '../../../global/typography'

// Helpers
import { isResourceCategory } from '../../../../utils/functions.utils'
import ImgUtil from '../../../../utils/img.utils'
import { isExternalURL } from '../../../../utils/link.utils'
import UrlUtil from '../../../../utils/url.utils'

// Constants
import { ModuleNames } from '../../../../constants/componentNames.constants'

// Styles
import { OPTIONS } from '../../../../constants/dateOptions'
import { LocaleCodes } from '../../../../constants/localization.constants'
import WrapperImage from '../../../global/wrapperImage'
import { ResourceItem } from './style'

const VideoPlayIcon = (props: { className: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      className={props.className}>
      <circle
        className="VideoPlayIcon__Circle"
        cx="28"
        cy="28"
        r="28"
        fill="white"
        fillOpacity="0.8"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M38 27.8134V28.1867C37.9989 28.5421 37.8117 28.871 37.5066 29.0534L25.5733 36.0001C24.8533 36.4267 24.48 36.4267 24.16 36.2401L23.8266 36.0534C23.5296 35.876 23.3436 35.5593 23.3333 35.2134V20.7867C23.3344 20.4313 23.5216 20.1024 23.8266 19.9201L24.16 19.7334C24.48 19.5467 24.8533 19.5467 25.7866 20.0934L37.5066 26.9467C37.8117 27.1291 37.9989 27.458 38 27.8134Z"
        fill="#FA4616"
      />
    </svg>
  )
}

export const ResourceCard = (
  item: any,
  i: number,
  isEvents: boolean,
  microcopyFeaturedText: string,
  microcopyOnDemand?: string,
  inEnglishText?: string,
  locale: string = LocaleCodes.ENGLISH.code
) => {
  if (item) {
    const resourceItem = item

    let categoryIcon = resourceItem.icon || ''
    let categoryAltText = resourceItem.iconAlt || ''
    let useCase = resourceItem.useCase || ''
    let isOnDemand

    const inEnglish = item.inEnglish && locale !== 'en-US'

    if (useCase === 'On Demand') {
      isOnDemand = true
    } else if (
      resourceItem[ModuleNames.TOPIC_WEBINAR_TYPE] &&
      Array.isArray(resourceItem[ModuleNames.TOPIC_WEBINAR_TYPE]) &&
      resourceItem[ModuleNames.TOPIC_WEBINAR_TYPE].includes('On-Demand')
    ) {
      isOnDemand = true
    } else if (
      resourceItem[ModuleNames.TOPIC_EVENT_TYPE] &&
      Array.isArray(resourceItem[ModuleNames.TOPIC_EVENT_TYPE]) &&
      resourceItem[ModuleNames.TOPIC_EVENT_TYPE].includes('On-Demand')
    ) {
      isOnDemand = true
    }
    const resourceCategory = resourceItem.category || ''
    const isVideoDemo = isResourceCategory('Video Demo', resourceItem.categoryNumber)
    const isCaseStudy = isResourceCategory('Case Study', resourceItem.categoryNumber)
    const resourceTitle = resourceItem.title
      ? resourceItem.title.replace(/<br\s*\\?>/g, '\r\n')
      : ''
    let resourceSubtitle = ''
    if (resourceItem.teaserBody || resourceItem.body) {
      if (resourceItem.teaserBody) {
        if (resourceItem?.teaserBody?.length > 180) {
          resourceSubtitle = resourceItem.teaserBody.substr(0, 180) + '...'
        } else if (resourceItem?.teaserBody) {
          resourceSubtitle = resourceItem.teaserBody
        }
      }
    }

    const resourceImageURL = resourceItem.teaserImage

    const resourceImageAlt = resourceItem.teaserAlt

    const resourceSlug = resourceItem.slug || ''

    let convertedDate
    let resourceItemDate

    const datePassed = resourceItem.date && new Date(resourceItem.date) >= new Date()
    if (resourceItem.date) {
      resourceItemDate = moment(resourceItem.date.split('T')[0], 'YYYY-MM-DD')
        .utc()
        .format('MMMM DD, YYYY')
      resourceItemDate = new Date(resourceItemDate).toLocaleDateString(locale, OPTIONS)

      if (datePassed) {
        convertedDate = resourceItemDate
      }
    }

    let atr: any = {}

    if (UrlUtil.isValidURL(resourceSlug)) {
      atr = {
        href: resourceSlug,
        target: isExternalURL(resourceSlug) ? '_blank' : '_self',
      }
    } else {
      const newSlug = `/${resourceSlug.charAt(0) === '/' ? resourceSlug.slice(1) : resourceSlug}`
      atr = {
        href: newSlug,
        target: isExternalURL(newSlug) ? '_blank' : '_self',
      }
    }

    let caseStudyLogoSrc, caseStudyLogoAlt
    if (isCaseStudy && resourceItem.CustomerLogo) {
      caseStudyLogoSrc = resourceItem?.CustomerLogo?.image?.file?.url
        ? resourceItem.CustomerLogo.image.file.url
        : ''
      caseStudyLogoAlt = resourceItem?.CustomerLogo?.altText
        ? resourceItem.CustomerLogo.altText
        : 'UiPath Customer Logo'
    }

    const resource = (
      <ResourceItem
        {...atr}
        key={`ResourceItem--${resourceSlug}-${i}`}
        isEvents={isEvents}
        isVideoDemo={isVideoDemo}>
        <div className="ResourceItem-Img-Container">
          <div className="ResourceItem-Pills-Container">
            {isOnDemand && (
              <div className="ResourceItem-Pill">
                <Typography type="microAlt">{microcopyOnDemand}</Typography>
              </div>
            )}
            {inEnglish && (
              <div className="ResourceItem-Pill">
                <Typography type="microAlt">{inEnglishText}</Typography>
              </div>
            )}
          </div>
          <div className="ResourceItem-Img-Overlay"></div>
          {categoryIcon && !isCaseStudy && !isVideoDemo && (
            <WrapperImage
              className="ResourceItem__Item-Icon"
              loading="lazy"
              src={ImgUtil.getResizedImageUrl(categoryIcon, '')}
              alt={categoryAltText}
              width={48}
              height={48}
              threshold={650}
            />
          )}
          {isVideoDemo && <VideoPlayIcon className="ResourceItem__Item-Icon" />}
          {isCaseStudy && caseStudyLogoSrc && (
            <WrapperImage
              className="ResourceItem__Item-Case-Study-Logo"
              loading="lazy"
              src={ImgUtil.getResizedImageUrl(caseStudyLogoSrc, '')}
              alt={caseStudyLogoAlt}
              width={175}
              height={88}
              threshold={650}
            />
          )}
          {resourceImageURL && (
            <WrapperImage
              className="ResourceItem-Img"
              src={ImgUtil.getResizedImageUrl(resourceImageURL, 'width=600')}
              loading="lazy"
              alt={resourceImageAlt}
              width={282}
              height={158}
              threshold={650}
            />
          )}
        </div>
        <div className="ResourceItem-Text">
          <div className="ResourceItem-Text-Category-Date">
            <div className="ResourceItemCategoryTextContainer">
              {resourceItem.featured && i < 6 && microcopyFeaturedText && (
                <Typography className="ResourceItem-Text-Featured" type="microAlt">
                  {microcopyFeaturedText}
                </Typography>
              )}
              {resourceCategory && (
                <Typography className="ResourceItem-Text-Category" type="captionAlt">
                  {resourceCategory}
                </Typography>
              )}
            </div>
            {convertedDate && (
              <Typography className="ResourceItem-Text-Date" type="caption">
                {convertedDate}
              </Typography>
            )}
          </div>
          {resourceTitle && (
            <Typography className="ResourceItem-Title" type="header6">
              <span dangerouslySetInnerHTML={{ __html: resourceTitle }}></span>
            </Typography>
          )}
          {resourceSubtitle && (
            <Typography className="ResourceItem-Subtitle" type="bodyXS">
              {resourceSubtitle}
            </Typography>
          )}
        </div>
      </ResourceItem>
    )

    return resource
  } else return null
}
